import { useState } from "react";
import axios from "../API/API";
import { FILE_SERVICE_BASE_URL } from "../constant/apiUrl";
import { validateThumbnailAspectRatio } from "../utils/function";
import fetchAndCacheImageName from "../utils/fetchAndCatchImageName";
import { showErrorToast, showSuccessToast } from "../utils/Toast/toast";

const useThumbnailUpload = ({
  validateAspectRatio = true,
  showSuccessMessage = true,
  showErrorMessage = true,
  onSuccess,
  onError,
} = {}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const uploadThumbnail = async (file) => {
    if (!file) {
      const errorMsg = "Thumbnail - No file selected";
      setError(errorMsg);
      if (showErrorMessage) {
        showErrorToast(errorMsg);
      }
      if (onError) {
        onError(new Error(errorMsg));
      }
      return null;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Validate aspect ratio if enabled
      if (validateAspectRatio) {
        // Pass showToast=false to prevent duplicate error messages
        // The hook will handle displaying the error message
        await validateThumbnailAspectRatio(file, false);
      }

      // Upload the thumbnail
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        `${FILE_SERVICE_BASE_URL}/fritolay/file-manager/thumbnail/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        const thumbnailFileId = response.data.fileId;

        // Fetch thumbnail CDN URL using fileId
        let cdnUrl = "";
        try {
          const thumbnailUrl = await fetchAndCacheImageName(thumbnailFileId);
          if (thumbnailUrl?.url) {
            cdnUrl = thumbnailUrl.url;
          }
        } catch (urlError) {
          console.error("Error fetching thumbnail URL:", urlError);
          // Continue even if thumbnail URL fetch fails
        }

        const result = {
          fileId: thumbnailFileId,
          cdnUrl: cdnUrl,
        };

        if (showSuccessMessage) {
          showSuccessToast("Thumbnail uploaded successfully");
        }

        if (onSuccess) {
          onSuccess(result);
        }

        setIsUploading(false);
        return result;
      } else {
        throw new Error("Thumbnail - Failed to upload thumbnail");
      }
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      // Use the original error message if it contains "aspect ratio" or already has "Thumbnail -" prefix to preserve detailed info
      const errorMessage =
        error.message && (error.message.includes("aspect ratio") || error.message.startsWith("Thumbnail -"))
          ? error.message // Use the detailed error message from validation
          : error.message ? `Thumbnail - ${error.message}` : "Thumbnail - Something went wrong while uploading thumbnail";

      setError(errorMessage);

      if (showErrorMessage) {
        showErrorToast(errorMessage);
      }

      if (onError) {
        onError(error);
      }

      setIsUploading(false);
      return null;
    }
  };

  return {
    uploadThumbnail,
    isUploading,
    error,
  };
};

export default useThumbnailUpload;

